import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { KeyboardEvent, useEffect, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DialpadIcon from "@mui/icons-material/Dialpad";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";
import { VoiceStatus, useGetVoiceDirectoryQuery } from "services/dialstack";

import {
  LILAC,
  PLUM_600,
  PLUM_900,
  WHITE_INK
} from "./dialstack-softphone-widget.styles";
import PresenceDot, {
  PRESENCE_POLL_MS,
  SUNO_STATUS_POLL_MS,
  statusLook
} from "./presence-status.component";
import useLivePresence, { liveStatus } from "./use-live-presence.hook";

export { PRESENCE_POLL_MS, SUNO_STATUS_POLL_MS };

// Something a call can be transferred to: a colleague from the directory, or
// the number that was typed.
type TransferTarget = {
  key: string;
  name: string;
  detail: string;
  // What the softphone dials. It strips this down to dial characters, so it is
  // always an extension or a number.
  destination: string;
  isColleague: boolean;
  status: VoiceStatus | null;
};

// The poll cadences, the status dot and its label live in
// presence-status.component.tsx, shared with the Rolodex's Team tab (ENG-2177).

const DIALABLE_PATTERN = /^[\d\s()+\-.*#]+$/;

const ACTION_SX = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: "none",
  py: 0.25,
  minWidth: 0
};

const hintSx = {
  fontSize: 13,
  color: WHITE_INK,
  textAlign: "center",
  py: 1
};

function TransferTargetRow({
  target,
  highlighted,
  disabled,
  onTransferNow,
  onConsultFirst
}: {
  target: TransferTarget;
  highlighted: boolean;
  disabled: boolean;
  onTransferNow: () => void;
  onConsultFirst: () => void;
}) {
  const rowRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (highlighted) rowRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [highlighted]);
  const look = statusLook(target.status);

  return (
    <Box
      component="li"
      ref={rowRef}
      data-testid={`transfer-target-${target.key}`}
      aria-current={highlighted ? "true" : undefined}
      sx={{
        listStyle: "none",
        px: 1.25,
        py: 1,
        borderRadius: "14px",
        bgcolor: "rgba(255, 255, 255, 0.96)",
        boxShadow: "0 6px 14px -8px hsla(271, 30%, 15%, 0.5)",
        outline: highlighted ? `2px solid ${LILAC}` : "none",
        outlineOffset: 1
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: LILAC }}>
            {target.isColleague ? (
              <PersonIcon sx={{ fontSize: 20, color: PLUM_600 }} />
            ) : (
              <DialpadIcon sx={{ fontSize: 18, color: PLUM_600 }} />
            )}
          </Avatar>
          <PresenceDot
            status={target.status}
            testIdPrefix="transfer-target-presence-"
          />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={{ fontSize: 14, fontWeight: 700, color: PLUM_900 }}
          >
            {target.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: 12, color: neutral[600] }}>
            {target.detail}
            {look && (
              <>
                {" · "}
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: neutral[800] }}
                >
                  {look.label}
                </Box>
              </>
            )}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
        <Button
          fullWidth
          size="small"
          variant="outlined"
          disabled={disabled}
          aria-label={`Transfer now to ${target.name}`}
          onClick={onTransferNow}
          sx={ACTION_SX}
        >
          Transfer now
        </Button>
        <Button
          fullWidth
          size="small"
          variant="contained"
          disabled={disabled}
          aria-label={`Consult ${target.name} first`}
          onClick={onConsultFirst}
          sx={ACTION_SX}
        >
          Consult first
        </Button>
      </Stack>
    </Box>
  );
}

// Replaces the softphone's bare "type an extension" transfer field: pick a
// colleague by name, or still type any extension or number. Rendered while the
// softphone's transfer overlay is open; every exit closes that overlay.
export default function TransferTargetPicker() {
  const { actions, overlays, consultCall, startAttendedTransfer, clearError } =
    useSoftphone();
  // The poll slows down once the live feed is up (a state, not the hook's
  // flag directly: the directory read has to be issued before the feed can
  // know whom to watch).
  const [pollingInterval, setPollingInterval] = useState(PRESENCE_POLL_MS);
  const {
    data: directory,
    isLoading,
    isError
  } = useGetVoiceDirectoryQuery(undefined, {
    pollingInterval,
    refetchOnMountOrArgChange: true
  });
  const { entries: livePresence, live } = useLivePresence(
    (directory ?? []).flatMap(({ user_id }) => (user_id ? [user_id] : []))
  );
  useEffect(() => {
    setPollingInterval(live ? SUNO_STATUS_POLL_MS : PRESENCE_POLL_MS);
  }, [live]);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  const term = query.trim();
  const needle = term.toLowerCase();
  const colleagues: TransferTarget[] = (directory ?? [])
    .filter(
      ({ name, extension }) =>
        name.toLowerCase().includes(needle) || extension.includes(needle)
    )
    .map(({ name, extension, presence, status, user_id }) => {
      const liveEntry = user_id ? livePresence[user_id] : undefined;
      return {
        key: `ext-${extension}`,
        name,
        detail: `Ext ${extension}`,
        destination: extension,
        isColleague: true,
        // The phone's live state once it has arrived; until then the last
        // read. A backend from before ENG-2022 sends no `status`.
        status: liveEntry
          ? liveStatus(liveEntry, presence, status)
          : (status ?? presence)
      };
    });

  // Typing digits always offers the number itself, so extensions that are not
  // in the directory and outside numbers stay reachable.
  const typedDigits = term.replace(/\D/g, "");
  const isTypedNumber =
    DIALABLE_PATTERN.test(term) &&
    typedDigits.length >= 2 &&
    !colleagues.some(({ destination }) => destination === typedDigits);
  const targets: TransferTarget[] = isTypedNumber
    ? [
        {
          key: "typed-number",
          name: term,
          detail: "Number you typed",
          destination: term,
          isColleague: false,
          status: null
        },
        ...colleagues
      ]
    : colleagues;
  const highlighted = targets[Math.min(highlightedIndex, targets.length - 1)];
  const highlightedLook = statusLook(highlighted?.status ?? null);

  const transferNow = (target: TransferTarget) => {
    if (actions.transfer(target.destination)) {
      overlays.closeTransfer();
      return;
    }
    setFailed(true);
  };

  // The softphone resolves a refused consult like a placed one (the refusal
  // goes to its own error channel), so the consult leg appearing is the only
  // sign that it worked.
  const { closeTransfer } = overlays;
  useEffect(() => {
    if (consultCall) closeTransfer();
  }, [consultCall, closeTransfer]);

  // The softphone also raises its own "Call failed" chip, which reads as if
  // the caller was lost. The picker already says what happened.
  const showFailed = failed && !consultCall;
  useEffect(() => {
    if (showFailed) clearError();
  }, [showFailed, clearError]);

  const consultFirst = async (target: TransferTarget) => {
    setIsStarting(true);
    setFailed(false);
    await startAttendedTransfer(target.destination);
    setFailed(true);
    setIsStarting(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      overlays.closeTransfer();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, targets.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && highlighted && !isStarting) {
      // Enter takes the reversible route: the caller is only handed over once
      // the colleague has picked up and the transfer is completed.
      event.preventDefault();
      consultFirst(highlighted);
    }
  };

  return (
    <Box
      data-suno-transfer
      data-testid="transfer-target-picker"
      role="group"
      aria-label="Transfer call"
      sx={{ px: 1.25, pt: 1.25 }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography
          sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: "common.white" }}
        >
          Transfer call
        </Typography>
        <IconButton
          size="small"
          aria-label="Cancel transfer"
          onClick={overlays.closeTransfer}
          sx={{ color: "common.white" }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
      <TextField
        fullWidth
        autoFocus
        size="small"
        placeholder="Name, extension or number"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setHighlightedIndex(0);
          setFailed(false);
        }}
        onKeyDown={handleKeyDown}
        inputProps={{
          "aria-label": "Search colleagues by name, extension or number",
          autoComplete: "off"
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: neutral[600] }} />
            </InputAdornment>
          )
        }}
        sx={{
          bgcolor: "common.white",
          borderRadius: "12px",
          "& .MuiOutlinedInput-root": { borderRadius: "12px" }
        }}
      />
      {showFailed && (
        <Typography
          role="alert"
          data-testid="transfer-target-failed"
          sx={{ ...hintSx, color: "common.white", fontWeight: 700 }}
        >
          That transfer didn&apos;t go through. Check the number and try again.
        </Typography>
      )}
      {isError && !directory && (
        <Typography
          role="status"
          data-testid="transfer-directory-error"
          sx={hintSx}
        >
          We couldn&apos;t load your team. Type an extension or number to
          transfer.
        </Typography>
      )}
      {targets.length > 0 && (
        <Stack
          component="ul"
          spacing={1}
          aria-label="Transfer targets"
          sx={{ m: 0, mt: 1, p: 0.375, maxHeight: 264, overflowY: "auto" }}
        >
          {targets.map((target) => (
            <TransferTargetRow
              key={target.key}
              target={target}
              highlighted={target === highlighted}
              disabled={isStarting}
              onTransferNow={() => transferNow(target)}
              onConsultFirst={() => consultFirst(target)}
            />
          ))}
        </Stack>
      )}
      {isLoading && (
        <Stack spacing={1} sx={{ mt: 1 }} data-testid="transfer-target-loading">
          <Skeleton
            variant="rounded"
            height={84}
            sx={{ borderRadius: "14px", bgcolor: "rgba(255,255,255,0.35)" }}
          />
          <Skeleton
            variant="rounded"
            height={84}
            sx={{ borderRadius: "14px", bgcolor: "rgba(255,255,255,0.25)" }}
          />
        </Stack>
      )}
      {!isLoading && !isError && targets.length === 0 && (
        <Typography
          role="status"
          data-testid="transfer-target-empty"
          sx={hintSx}
        >
          {term
            ? `No colleague matches “${term}”`
            : "No colleagues have an extension yet. Type a number to transfer."}
        </Typography>
      )}
      {highlighted && (
        // Focus stays in the search box, so this line is also how a screen
        // reader hears which row the arrow keys are on.
        <Typography
          aria-live="polite"
          data-testid="transfer-target-hint"
          sx={{ ...hintSx, fontSize: 12, py: 0.5 }}
        >
          Enter consults {highlighted.name} first
          {highlightedLook && ` (${highlightedLook.label.toLowerCase()})`}
          {" · Esc cancels"}
        </Typography>
      )}
    </Box>
  );
}
